import type {
  ActionResult,
  ExportFileListItem,
  ExportFileSource,
  ExportFileStatus,
  FlushQueueResult,
  FtpAccount,
  ManualUploadResult,
  Paginated,
} from '@flux/shared';
import { apiFetch, downloadFromApi } from '@/lib/api/client.js';

/**
 * Couche réseau de l'écran Fichiers (`02` §5). Les clés de requête sont
 * locales à cette fonctionnalité (pas d'ajout à `lib/query/keys.ts`, hors
 * périmètre de cette vague — voir le rapport de la vague).
 */
export const filesQueryKeys = {
  all: ['files'] as const,
  list: (params: FilesListParams) => ['files', 'list', params] as const,
  queuedCount: () => ['files', 'queued-count'] as const,
};

export interface FilesListParams {
  page: number;
  pageSize: number;
  sortDir: 'asc' | 'desc';
  sortBy?: string;
  source?: ExportFileSource;
  ftpAccount?: FtpAccount;
  status?: ExportFileStatus[];
  store?: string;
  from?: string;
  to?: string;
  search?: string;
}

export function fetchFilesList(params: FilesListParams): Promise<Paginated<ExportFileListItem>> {
  return apiFetch<Paginated<ExportFileListItem>>('/api/files', {
    query: {
      page: params.page,
      pageSize: params.pageSize,
      sortDir: params.sortDir,
      ...(params.sortBy ? { sortBy: params.sortBy } : {}),
      ...(params.source ? { source: params.source } : {}),
      ...(params.ftpAccount ? { ftpAccount: params.ftpAccount } : {}),
      ...(params.status && params.status.length > 0 ? { status: params.status } : {}),
      ...(params.store ? { store: params.store } : {}),
      ...(params.from ? { from: params.from } : {}),
      ...(params.to ? { to: params.to } : {}),
      ...(params.search ? { search: params.search } : {}),
    },
  });
}

/** Nombre de fichiers `queued` toutes boutiques — alimente le bouton « Envoyer la file » (`02` §5). */
export function fetchQueuedFilesCount(): Promise<number> {
  return apiFetch<Paginated<ExportFileListItem>>('/api/files', {
    query: { status: ['queued'], page: 1, pageSize: 1 },
  }).then((result) => result.total);
}

export function downloadExportFile(id: string): void {
  downloadFromApi(`/api/files/${id}/download`);
}

export function resendExportFile(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/files/${id}/resend`, { method: 'POST' });
}

export function removeQueuedFile(id: string): Promise<ActionResult> {
  return apiFetch<ActionResult>(`/api/files/${id}/queued`, { method: 'DELETE' });
}

export interface UploadCsvInput {
  fileName: string;
  ftpAccount: FtpAccount;
  contentBase64: string;
}

export function uploadManualCsv(body: UploadCsvInput): Promise<ManualUploadResult> {
  return apiFetch<ManualUploadResult>('/api/files/upload', { method: 'POST', body });
}

export function flushFileQueue(): Promise<FlushQueueResult> {
  return apiFetch<FlushQueueResult>('/api/files/flush', { method: 'POST' });
}

/** Lit un `File` du navigateur en base64 (`ManualUploadBody.contentBase64`). */
export function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onerror = () => {
      reject(new Error('Lecture du fichier impossible.'));
    };
    reader.onload = () => {
      const result = reader.result;
      if (typeof result !== 'string') {
        reject(new Error('Lecture du fichier impossible.'));
        return;
      }
      const commaIndex = result.indexOf(',');
      resolve(commaIndex >= 0 ? result.slice(commaIndex + 1) : result);
    };
    reader.readAsDataURL(file);
  });
}
